import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import FormField from '@cloudscape-design/components/form-field';
import Multiselect from '@cloudscape-design/components/multiselect';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import {
  isEuCountry,
  enabledPspChoices,
  PaymentMode,
  PaymentProvider,
  SellerEntityStatus,
  type PspCatalogEntry,
  type SellerEntity,
} from '@etiquetachef/types';
import { subscribeSellerEntities } from '@/lib/sellerEntities';
import { subscribePspConfigs } from '@/lib/firestoreTenantDetail';
import { marketPspPayload, splitPspChoice, type MarketPspValue } from './marketPspValue';

type Props = {
  readonly country: string;
  readonly value: MarketPspValue;
  readonly onChange: (v: MarketPspValue) => void;
};

/**
 * Os campos de PSP de um mercado vendável: PSP ativo × modo, PSPs também
 * permitidos por venda, fiscal e entidade vendedora (os dois derivados do PSP)
 * — com os avisos de sandbox e do SDI italiano.
 *
 * Origem: #756 — 2026-10-01 11:33 +0200 (A7: "no admin posso mudar de PSP por
 * país; ao mudar o país já passa a cobrar no PSP selecionado; e pode ser
 * sandbox também para testes", owner).
 *
 * Por quê: duas telas editam o mesmo doc pelo mesmo callable — a aba Países de
 * `/countries` (`CountryEditDialog`, a do menu) e `/billing/markets`
 * (`MarketConfigEditDialog`). Cada uma tinha a sua cópia do seletor e dos
 * guards (UE→Paddle), e as duas esconderam o Asaas. Um componente só, com a
 * regra de `marketPspChoices` (`@etiquetachef/types`): Asaas só no BR, Stripe
 * em todo país.
 *
 * Contrato: controlado (`value`/`onChange`); `marketPspPayload`
 * (`marketPspValue.ts`) monta o input.
 * Assina `/config/psps`: o seletor mostra só o PSP × modo ATIVO em `/psp`
 * (`enabledPspChoices`, owner 2026-10-01, #756) — o que o país já tinha
 * gravado continua como "atual". Assina `/config/sellerEntities` só para MOSTRAR
 * a PJ do PSP × modo escolhido (`sellerForPsp`, espelho de
 * `findSellerEntityForProvider`) — o país não escolhe entidade desde a #958.
 * O servidor revalida tudo (`setMarketConfig`).
 *
 * Usado por: `features/countries/CountryEditDialog.tsx`,
 * `features/billing/MarketConfigEditDialog.tsx`.
 *
 * Dicas: o fiscal não é escolhido — `defaultFiscalProviderFor` (Asaas →
 * `ASAAS_NFSE`, Stripe → `STRIPE_HOSTED`); o servidor recusa Asaas com outro
 * fiscal (`fiscal_provider_mismatch`) e PSP × modo sem entidade ATIVA
 * (`psp_without_seller`, #958).
 *
 * Histórico:
 *  - 2026-10-13 11:23 +0200 — (#958) seletor de entidade trocado pela PJ
 *    derivada do PSP (owner: "países são definidos em PSPs").
 */
export function MarketPspFields({ country, value, onChange }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [entities, setEntities] = useState<SellerEntity[]>([]);
  const [catalog, setCatalog] = useState<PspCatalogEntry[] | null>(null);

  useEffect(
    () =>
      subscribePspConfigs((r) => {
        if (r.kind === 'READY') setCatalog(r.psps);
      }),
    [],
  );

  useEffect(
    () =>
      subscribeSellerEntities((r) => {
        if (r.kind === 'READY') setEntities(r.entities);
      }),
    [],
  );

  const choices = enabledPspChoices(country, catalog ?? []);
  const { provider, mode } = splitPspChoice(value.choice);
  const choiceOptions = choices.map((c) => ({
    value: `${c.provider}:${c.mode}`,
    label: t(`billing.markets.pspChoice.${c.provider}.${c.mode}`),
  }));
  // O PSP gravado fora do seletor (Paddle da IT, legado) continua como opção
  // "atual" — salvar outro campo do país não pode trocá-lo (auditoria #756).
  if (value.initial && !choiceOptions.some((o) => o.value === value.initial?.choice)) {
    const legacy = splitPspChoice(value.initial.choice);
    choiceOptions.unshift({
      value: value.initial.choice,
      label: t('billing.markets.pspCurrent', { provider: legacy.provider, mode: legacy.mode }),
    });
  }
  // Permitidos por venda: PSPs ativos NO MESMO MODO do ativo (o país tem um modo só).
  const extraOptions = [...new Set(choices.filter((c) => c.mode === mode).map((c) => c.provider))]
    .filter((p) => p !== provider)
    .map((p) => ({ value: p, label: t(`billing.markets.provider.${p}`) }));
  // Auditoria 2026-10-01 (#756, M5): permitido que não aparece no seletor
  // (desligado em /psp, ou de outro modo) ia no payload sem o admin poder
  // tirá-lo — e o servidor recusava (`psp_mode_disabled`). Com o catálogo
  // carregado, sai do valor.
  const offered = extraOptions.map((o) => o.value).join(',');
  useEffect(() => {
    if (catalog === null) return;
    const keep = value.extraAllowed.filter((p) => offered.split(',').includes(p));
    if (keep.length !== value.extraAllowed.length) onChange({ ...value, extraAllowed: keep });
  }, [catalog, offered, value, onChange]);
  // #958 — a PJ do país é a entidade ATIVA do PSP × modo, como o servidor resolve.
  const seller = sellerForPsp(entities, provider, mode);

  return (
    <SpaceBetween size="m">
      <FormField label={t('billing.markets.pspActive')} description={t('billing.markets.pspActiveDescription')}>
        <Select
          selectedOption={choiceOptions.find((o) => o.value === value.choice) ?? null}
          options={choiceOptions}
          placeholder={t('billing.markets.paymentProviderPlaceholder')}
          onChange={({ detail }): void => {
            onChange({ ...value, choice: detail.selectedOption.value ?? value.choice });
          }}
        />
      </FormField>

      {extraOptions.length > 0 && (
        <FormField label={t('billing.markets.alsoAllowed')} description={t('billing.markets.alsoAllowedDescription')}>
          <Multiselect
            selectedOptions={extraOptions.filter((o) => value.extraAllowed.includes(o.value))}
            options={extraOptions}
            placeholder={t('billing.markets.alsoAllowedPlaceholder')}
            onChange={({ detail }): void => {
              onChange({ ...value, extraAllowed: detail.selectedOptions.flatMap((o) => (o.value ? [o.value] : [])) });
            }}
          />
        </FormField>
      )}

      <FormField label={t('billing.markets.fiscalProvider')} description={t('billing.markets.fiscalDerived')}>
        <Box variant="code">{marketPspPayload(value).fiscal_provider}</Box>
      </FormField>

      <FormField label={t('billing.markets.sellerEntity')}>
        {seller ? (
          <Box>{`${seller.id} · ${seller.legal_name}`}</Box>
        ) : (
          <Alert type="warning">{t('billing.markets.noSellerEntities')}</Alert>
        )}
      </FormField>

      {mode === PaymentMode.SANDBOX && <Alert type="warning">{t('billing.markets.sandboxWarning')}</Alert>}
      {country === 'IT' && provider === PaymentProvider.STRIPE && (
        <Alert type="warning">{t('billing.markets.itNoteSdi')}</Alert>
      )}
      {isEuCountry(country) && country !== 'IT' && provider === PaymentProvider.STRIPE && (
        <Alert type="info">{t('billing.markets.euNotePaddle')}</Alert>
      )}
      <Box color="text-status-inactive" fontSize="body-s">
        {t('billing.markets.effectNow')}
      </Box>
    </SpaceBetween>
  );
}

/**
 * A entidade ATIVA dona do PSP × modo — espelho, só para exibir, de
 * `findSellerEntityForProvider` (`apps/functions/src/lib/billing/asaasSellerEntity.ts`):
 * a do modo exato, senão a sem modo declarado; para o Asaas, sem nenhuma do
 * PSP, a ativa com sede no BR sem PSP (legado).
 *
 * Origem: #958 — 2026-10-13 11:23 +0200 (país → PSP → entidade).
 *
 * Usado por: `MarketPspFields` (acima). O servidor é quem decide
 * (`psp_without_seller` no `setMarketConfig`); divergir aqui só erra o rótulo.
 */
function sellerForPsp(entities: readonly SellerEntity[], provider: string, mode: PaymentMode): SellerEntity | null {
  const active = entities.filter((e) => e.status === SellerEntityStatus.ACTIVE);
  const own = active.filter((e) => e.payment_provider === provider);
  const exact = own.find((e) => e.payment_mode === mode);
  if (exact) return exact;
  const anyMode = own.find((e) => !e.payment_mode);
  if (anyMode) return anyMode;
  if (own.length > 0 || provider !== PaymentProvider.ASAAS) return null;
  return active.find((e) => !e.payment_provider && e.headquarter_country === 'BR') ?? null;
}
